/**
 * Maps a forex/crypto/index symbol to one or two ISO 3166-1 alpha-2 country codes
 * used to render country flag icons.
 *
 * For forex pairs the first code is the base currency and the second is the quote.
 * For crypto and indices only a single "brand" code is returned (or null for no flag).
 */

const CURRENCY_TO_COUNTRY: Record<string, string> = {
  USD: 'US',
  EUR: 'EU',
  GBP: 'GB',
  JPY: 'JP',
  AUD: 'AU',
  CAD: 'CA',
  CHF: 'CH',
  NZD: 'NZ',
  SGD: 'SG',
  HKD: 'HK',
  NOK: 'NO',
  SEK: 'SE',
  DKK: 'DK',
  MXN: 'MX',
  ZAR: 'ZA',
  TRY: 'TR',
  CNH: 'CN',
  CNY: 'CN',
  PLN: 'PL',
  CZK: 'CZ',
  HUF: 'HU',
  RUB: 'RU',
  INR: 'IN',
  BRL: 'BR',
  KRW: 'KR',
  THB: 'TH',
  IDR: 'ID',
  MYR: 'MY',
  PHP: 'PH',
};

/** Known forex quote currencies (3-letter codes that end forex pairs) */
const QUOTE_CURRENCIES = [
  'USD',
  'EUR',
  'GBP',
  'JPY',
  'AUD',
  'CAD',
  'CHF',
  'NZD',
  'SGD',
  'HKD',
  'NOK',
  'SEK',
  'DKK',
  'MXN',
  'ZAR',
  'TRY',
  'CNH',
  'CNY',
  'PLN',
  'CZK',
  'HUF',
  'RUB',
  'INR',
  'BRL',
  'KRW',
  'THB',
  'IDR',
  'MYR',
  'PHP',
];

export interface SymbolFlags {
  /** Primary (foreground) flag country code */
  primary: string;
  /** Secondary (background) flag country code, undefined for single-flag symbols */
  secondary?: string;
}

/**
 * Attempts to split a symbol string into [baseCurrency, quoteCurrency].
 * Returns null if the symbol is not a recognised forex pair.
 */
const splitForexPair = (symbol: string): [string, string] | null => {
  const upper = symbol.toUpperCase();
  for (const quote of QUOTE_CURRENCIES) {
    if (upper.endsWith(quote) && upper.length > quote.length) {
      const base = upper.slice(0, upper.length - quote.length);
      if (CURRENCY_TO_COUNTRY[base] && CURRENCY_TO_COUNTRY[quote]) {
        return [base, quote];
      }
    }
  }
  return null;
};

/**
 * Formats a symbol for display. Forex pairs get a slash: EURUSD → EUR/USD.
 * Non-forex symbols are returned as-is.
 */
export const formatSymbolDisplay = (symbol: string): string => {
  const pair = splitForexPair(symbol);
  if (!pair) return symbol;
  return `${pair[0]}/${pair[1]}`;
};

/**
 * Returns flag country codes for a given trading symbol.
 * - Forex pairs  → { primary: baseFlagCode, secondary: quoteFlagCode }
 * - Everything else → undefined (no flags)
 */
export const getSymbolFlags = (symbol: string): SymbolFlags | undefined => {
  const pair = splitForexPair(symbol);
  if (!pair) return undefined;

  const [base, quote] = pair;
  return {
    primary: CURRENCY_TO_COUNTRY[base],
    secondary: CURRENCY_TO_COUNTRY[quote],
  };
};

/**
 * Maps known non-forex symbols (crypto, commodities) to their local asset icon key.
 * Keys are uppercased exact symbols; values match the keys in ASSET_ICON_COMPONENTS.
 */
const SYMBOL_ICON_MAP: Record<string, string> = {
  XAUUSD: 'Gold',
  BTCUSD: 'Btc',
  ETHUSD: 'Eth',
};

/**
 * Returns a local asset icon key for symbols that have a dedicated icon (crypto / commodities).
 * Returns undefined for everything else (forex pairs use flags, rest use abbreviation circle).
 */
export const getSymbolIcon = (symbol: string): string | undefined =>
  SYMBOL_ICON_MAP[symbol.toUpperCase()];

/**
 * Returns the fallback label to display inside the abbreviation circle for symbols
 * that have no flag or dedicated icon.
 *
 * Rules (in priority order):
 *  1. If the symbol ends with digits, return those digits (e.g. "US30" → "30").
 *  2. Otherwise return the provided abbreviation as-is.
 */
export const getSymbolFallbackLabel = (
  symbol: string,
  abbreviation: string,
): string => {
  const match = symbol.match(/(\d+)$/);
  return match ? match[1] : abbreviation;
};
